import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  date: string;
  readTime: string;
  category: string;
  image: string;
  featured: boolean;
  tags: string[];
}

const blogPosts: BlogPost[] = [
  {
    id: "top-5-benefits-professional-rough-framing",
    title: "Top 5 Benefits of Professional Rough Framing",
    excerpt: "Discover why hiring a professional rough framing contractor is crucial for your construction project's success and longevity.",
    content: "When it comes to building or renovating your home, the framing stage is the foundation of everything that follows. Professional rough framing isn't just about putting up walls—it's about creating a structure that will stand the test of time. Here are the top 5 benefits of choosing professional rough framing services...",
    author: "Michael Rodriguez",
    date: "March 15, 2024",
    readTime: "6 min read",
    category: "Tips & Advice",
    image: "/images/blog-choosing-contractor.jpg",
    featured: true,
    tags: ["Professional Framing", "Quality", "Benefits", "Construction Tips"]
  },
  {
    id: "how-choose-framing-contractor-orange-county",
    title: "How to Choose a Framing Contractor in Orange County, CA",
    excerpt: "Essential tips for selecting a reliable and skilled framing contractor in Orange County, California.",
    content: "Choosing the right framing contractor in Orange County can make or break your construction project. With so many options available, it's crucial to know what to look for. Here's your comprehensive guide to finding the perfect framing contractor for your project...",
    author: "Sarah Johnson",
    date: "March 10, 2024",
    readTime: "8 min read",
    category: "Tips & Advice",
    image: "/images/blog-safety-practices.jpg",
    featured: true,
    tags: ["Orange County", "Contractor Selection", "Local Services", "Tips"]
  },
  {
    id: "rough-framing-cost-guide-2024",
    title: "Rough Framing Cost Guide 2024: What to Expect",
    excerpt: "Complete breakdown of rough framing costs in 2024, including factors that affect pricing and how to budget effectively.",
    content: "Understanding rough framing costs is essential for any construction project. In 2024, prices have been influenced by material costs, labor shortages, and market demand. Here's everything you need to know about rough framing costs and how to budget effectively...",
    author: "David Chen",
    date: "March 5, 2024",
    readTime: "7 min read",
    category: "Cost & Budget",
    image: "/images/blog-cost-estimation.jpg",
    featured: true,
    tags: ["Cost Guide", "Budgeting", "2024 Prices", "Framing Costs"]
  },
  {
    id: "rough-framing-vs-finished-framing",
    title: "Rough Framing vs Finished Framing: Understanding the Difference",
    excerpt: "Learn the key differences between rough framing and finished framing, and which one you need for your project.",
    content: "Many homeowners and builders confuse rough framing with finished framing, but understanding the difference is crucial for project planning and budgeting. Rough framing is the structural skeleton of your building, while finished framing includes the final touches...",
    author: "Jennifer Martinez",
    date: "February 28, 2024",
    readTime: "5 min read",
    category: "Education",
    image: "/images/blog-wood-vs-steel.jpg",
    featured: false,
    tags: ["Rough Framing", "Finished Framing", "Construction Types", "Education"]
  },
  {
    id: "rough-framing-timeline-complete-guide",
    title: "Rough Framing Timeline: Complete Guide for Your Project",
    excerpt: "How long does rough framing take? Get a detailed timeline for different project types and sizes.",
    content: "One of the most common questions we receive is 'How long will the rough framing take?' The answer depends on several factors including project size, complexity, weather conditions, and crew size. Here's a comprehensive guide to rough framing timelines...",
    author: "Michael Rodriguez",
    date: "February 20, 2024",
    readTime: "6 min read",
    category: "Project Planning",
    image: "/images/blog-commercial-trends.jpg",
    featured: false,
    tags: ["Timeline", "Project Planning", "Construction Schedule", "Framing Time"]
  },
  {
    id: "rough-framing-inspection-checklist",
    title: "Rough Framing Inspection Checklist: What Inspectors Look For",
    excerpt: "Essential checklist for rough framing inspections to ensure your project meets building codes and standards.",
    content: "Rough framing inspections are critical checkpoints in any construction project. They ensure that the structural framework meets building codes and safety standards before proceeding with electrical, plumbing, and finishing work...",
    author: "David Chen",
    date: "February 15, 2024",
    readTime: "7 min read",
    category: "Inspections",
    image: "/images/blog-permits-codes.jpg",
    featured: false,
    tags: ["Inspections", "Building Codes", "Safety", "Checklist"]
  },
  {
    id: "rough-framing-materials-comparison",
    title: "Rough Framing Materials Comparison: Wood vs Steel vs Engineered",
    excerpt: "Compare different rough framing materials to choose the best option for your project's needs and budget.",
    content: "Choosing the right materials for rough framing is one of the most important decisions in your construction project. Each material has its advantages and considerations. Here's a comprehensive comparison of wood, steel, and engineered materials...",
    author: "Sarah Johnson",
    date: "February 10, 2024",
    readTime: "8 min read",
    category: "Materials",
    image: "/images/blog-sustainable-framing.jpg",
    featured: false,
    tags: ["Materials", "Wood Framing", "Steel Framing", "Engineered Lumber"]
  },
  {
    id: "rough-framing-safety-best-practices",
    title: "Rough Framing Safety Best Practices for 2024",
    excerpt: "Updated safety guidelines and best practices for rough framing projects to protect workers and ensure quality.",
    content: "Safety should always be the top priority on any construction site, especially during rough framing work. With new technologies and updated regulations, safety practices continue to evolve. Here are the latest safety best practices for rough framing...",
    author: "Jennifer Martinez",
    date: "February 5, 2024",
    readTime: "6 min read",
    category: "Safety",
    image: "/images/blog-safety-practices.jpg",
    featured: false,
    tags: ["Safety", "Best Practices", "Construction Safety", "2024 Guidelines"]
  }
];

const categories = [
  "All Posts",
  "Tips & Advice",
  "Cost & Budget",
  "Project Planning",
  "Materials",
  "Safety",
  "Inspections",
  "Education"
];

const Blog: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState("All Posts");
  const [searchTerm, setSearchTerm] = useState("");

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === "All Posts" || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const featuredPosts = blogPosts.filter(post => post.featured);

  return (
    <div className="min-h-screen bg-black">
      <Helmet>
        <title>Blog | Rough Framing Co. | Industry Insights & Tips</title>
        <meta name="description" content="Expert insights, tips, and industry knowledge from Rough Framing Co. Learn about framing techniques, safety practices, and construction trends." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-[60vh] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url(/images/blog-hero.jpg)" }}
        >
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white max-w-4xl mx-auto px-6">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
              Blog & Insights
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90 font-light text-amber-100">
              Expert knowledge, tips, and industry insights from our team
            </p>
            <Link to="/contact">
              <button className="bg-amber-800 hover:bg-amber-700 text-white font-bold py-4 px-10 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-amber-600/50 shadow-2xl border border-amber-600">
                Get Expert Advice
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Posts */}
      <section className="py-24 bg-gray-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              Featured Articles
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Our most popular and informative posts
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {featuredPosts.map((post, index) => (
              <article
                key={post.id}
                className="bg-gray-800 rounded-2xl shadow-2xl hover:shadow-amber-900/20 transition-all duration-500 transform hover:-translate-y-2 overflow-hidden border border-gray-700 hover:border-amber-600/30 group"
              >
                <div className="h-48 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${post.image})` }}>
                  <div className="h-full bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute top-4 left-4">
                    <span className="bg-amber-800 text-white px-3 py-1 rounded-full text-sm font-semibold border border-amber-600">
                      Featured
                    </span>
                  </div>
                </div>
                <div className="p-8">
                  <div className="flex items-center text-sm text-gray-400 mb-4">
                    <span>{post.date}</span>
                    <span className="mx-2">•</span>
                    <span>{post.readTime}</span>
                    <span className="mx-2">•</span>
                    <span className="text-amber-400">{post.category}</span>
                  </div>
                  <h3 className="text-xl font-bold text-amber-400 mb-4 group-hover:text-amber-300 transition-colors leading-tight">
                    {post.title}
                  </h3>
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-400">
                      By {post.author}
                    </div>
                    <Link to={`/blog/${post.id}`} className="text-amber-500 hover:text-amber-400 font-semibold transition-colors group-hover:translate-x-2 inline-block">
                      Read More →
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="py-12 bg-black border-b border-gray-800">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row gap-6 items-center justify-between">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 pl-12 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300"
                />
                <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 ${
                    selectedCategory === category
                      ? "bg-amber-800 text-white shadow-2xl border border-amber-600"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* All Posts */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              All Articles
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Browse our complete collection of framing insights and tips
            </p>
          </div>
          
          {filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-2xl font-bold text-amber-400 mb-4">No articles found</h3>
              <p className="text-gray-300">Try adjusting your search terms or category filter.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <article
                  key={post.id}
                  className="bg-gray-800 rounded-2xl shadow-2xl hover:shadow-amber-900/20 transition-all duration-500 transform hover:-translate-y-2 overflow-hidden border border-gray-700 hover:border-amber-600/30 group"
                >
                  <div className="h-48 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${post.image})` }}>
                    <div className="h-full bg-gradient-to-t from-black/60 to-transparent"></div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center text-sm text-gray-400 mb-3">
                      <span>{post.date}</span>
                      <span className="mx-2">•</span>
                      <span>{post.readTime}</span>
                    </div>
                    <div className="mb-3">
                      <span className="bg-gray-700 text-amber-400 px-2 py-1 rounded text-xs font-semibold">
                        {post.category}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-amber-400 mb-3 group-hover:text-amber-300 transition-colors leading-tight">
                      {post.title}
                    </h3>
                    <p className="text-gray-300 mb-4 leading-relaxed text-sm">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-400">
                        By {post.author}
                      </div>
                      <Link to={`/blog/${post.id}`} className="text-amber-500 hover:text-amber-400 font-semibold text-sm transition-colors group-hover:translate-x-2 inline-block">
                        Read More →
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-24 bg-gray-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              Stay Updated
            </h2>
            <p className="text-xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
              Subscribe to our newsletter for the latest framing tips, industry insights, and project updates
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300"
              />
              <button className="bg-amber-800 hover:bg-amber-700 text-white font-bold py-3 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-amber-600/50 shadow-xl border border-amber-600">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
            Need Expert Framing Services?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
            Ready to put our expertise to work on your project? Contact us for a free consultation and quote.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <button className="bg-amber-800 hover:bg-amber-700 text-white font-bold py-4 px-10 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-amber-600/50 shadow-2xl border border-amber-600">
                Get Free Quote
              </button>
            </Link>
            <a href="tel:+1234567890">
              <button className="bg-transparent hover:bg-gray-800 text-white font-bold py-4 px-10 rounded-lg text-lg transition-all duration-300 border-2 border-amber-600 hover:border-amber-500 focus:outline-none focus:ring-4 focus:ring-amber-600/50">
                Call (123) 456-7890
              </button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog; 